'use client';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

import { CommunityMessageProvider } from '../../contexts/community-message/provider';
import { useCommunityMessageContext } from '../../contexts/community-message/useCommunityMessageContext';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import type { MessageInteractionMode } from '../../types/MessageInteractionMode';
import { ChannelComposer } from '../channel-message-editor/ChannelComposer';
import { ChannelMessageBubble } from './ChannelMessageBubble';

interface Props {
  /** The message whose replies are shown inline. */
  message: ExtendedCommunityChannelMessage;
  currentUserId: string | null | undefined;
  channelSlug?: string;
  mode?: MessageInteractionMode;
  /** When true, the inline reply composer is shown at the bottom. */
  replying: boolean;
  onCancelReply: () => void;
  /** Collapse the whole replies subtree (unmounts this + its provider). */
  onCollapse: () => void;
}

// Small page so "Show More Replies" paces the reveal (matches upstream's 5).
const kInlineRepliesPageSize = 5;

/**
 * Replies rendered INLINE beneath a message — used in sidebar surfaces
 * (bookmarks / reports) where a full-screen thread pane can't open. Mounts a
 * `CommunityMessageProvider` scoped to this message as the thread parent, so it
 * reuses the exact same fetch + realtime + send pipeline as the thread pane;
 * only the presentation (expand/collapse, "Show More Replies", inline composer)
 * differs. Each reply is itself an inline-mode bubble, so replies-to-replies
 * expand recursively up to the model's 3-level limit. Web port of PopshopRN's
 * `CommunityChannelMessageReplies`.
 */
export function ChannelInlineReplies(props: Props) {
  return (
    <CommunityMessageProvider
      channelId={props.message.channelId}
      parentMessageId={props.message.id}
      pageSize={kInlineRepliesPageSize}
    >
      <InlineRepliesContent {...props} />
    </CommunityMessageProvider>
  );
}

function InlineRepliesContent({
  message,
  currentUserId,
  channelSlug,
  mode = 'normal',
  replying,
  onCancelReply,
  onCollapse,
}: Props) {
  const { messages$, state$, loadMore, sendMessage } =
    useCommunityMessageContext();
  const [replies] = useBehaviorSubject(messages$);
  const [state] = useBehaviorSubject(state$);

  // Spinner while the first page is still in flight for a message we KNOW has
  // replies (avoids a flash of the empty controls row before the fetch lands).
  const showInitialSpinner =
    state.isLoading && replies.length === 0 && message.replyCount > 0;
  const showControls = replies.length > 0 && !state.isLoadingMore;

  return (
    <div
      // Built-in `neutral-700` (#404040) reads clearly against BOTH a normal
      // message background (#171717) and a pinned message's warm `stone-800`
      // (#292524); a darker line would blend into the pinned background.
      className="border-line-strong mt-2 flex flex-col gap-2 border-l pl-3"
    >
      {replies.map(reply => (
        <ChannelMessageBubble
          key={reply.id}
          message={reply}
          currentUserId={currentUserId}
          channelSlug={channelSlug}
          mode={mode}
          repliesMode="inline"
        />
      ))}

      {showInitialSpinner || state.isLoadingMore ? (
        <Loader2
          aria-label="Loading replies"
          className="text-content-4 size-4 animate-spin"
        />
      ) : null}

      {showControls ? (
        <div className="flex items-center gap-4 text-xs">
          {state.hasMore ? (
            <button
              type="button"
              onClick={loadMore}
              className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1"
            >
              Show More Replies
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCollapse}
            className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1"
          >
            Collapse
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}

      {replying && mode === 'normal' ? (
        <div className="flex flex-col gap-1">
          <ChannelComposer
            onSend={async payload => {
              await sendMessage(payload);
              onCancelReply();
            }}
            channelId={message.channelId}
            placeholder={`Reply to @${replyTargetLabel(message)}…`}
          />
          <button
            type="button"
            onClick={onCancelReply}
            className="hover:text-content-1 text-content-3 self-start text-xs"
          >
            Cancel
          </button>
        </div>
      ) : null}
    </div>
  );
}

function replyTargetLabel(message: ExtendedCommunityChannelMessage): string {
  const { profile } = message;
  if (profile.username) return profile.username;
  const composed = `${profile.firstName?.trim() ?? ''} ${
    profile.lastName?.trim() ?? ''
  }`.trim();
  return composed || 'user';
}
